import React, { useEffect } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useRouter } from 'next/router';
import useHideScrollBar from '@fuww/library/src/lib/useHideScrollBar';
import { useSiteContext } from '../components/SiteContext';
import isProduction from '../lib/isProduction';
import { AuthProvider } from '../components/AuthProvider';
import withApollo from '../lib/apollo';
import withRestrictedAccess from '../lib/withRestrictedAccess';
import Layout from '../components/Dashboard/Layout/Dynamic';
import { Router } from '../routes.mjs';

interface RetoolPageInfo {
  id: string;
  title: string;
}

type RetoolPages = Record<string, RetoolPageInfo[]>;

const retoolPages: RetoolPages = {
  users: [{
    id: '3ad71dca-8204-48c3-90f8-da8bbbbb4b21',
    title: 'Users',
  }],
  open_ai: [{
    id: '6befb900-4a09-4b41-8fdd-73c312fd5593',
    title: 'Open AI',
  }],
  published_jobs: [{
    id: '5710ae73-3b3e-4734-bdca-e70f9a4276ec',
    title: 'Published Jobs',
  }],
  special_pages: [{
    id: 'e0f630e6-976e-4964-ae74-95e2627a80c2',
    title: 'Special Pages',
  }],
  tags: [{
    id: '9f99e0d7-ee9c-4fca-a25d-04dfaecee241',
    title: 'Tags',
  }],
  accounts: [{
    id: '79937eed-959e-4021-b80e-68e4b73bec2f',
    title: 'Profile and job accounts',
  }],
  brands: [{
    id: '21357614-a92b-460a-a96b-ff4085e5b5b3',
    title: 'Brands',
  }],
  category_keywords: [{
    id: '949a30af-191a-4670-9d20-088916c3eaf0',
    title: 'Category keywords table',
  }],
  lookbook_images: [{
    id: '497fefb7-ea1a-462f-94d4-e2f9b8cea763',
    title: 'Lookbook Images',
  }],
  marketplace_kpi: [{
    id: '0c90e16e-c629-4742-9c20-4daf34f379ca',
    title: 'Marketplace KPI',
  }],
  translate: [{
    id: '0693a180-cf49-45f5-9ead-c47fb4df7cd1',
    title: 'Translate',
  }],
  credits: [
    {
      id: '951abdeb-ed18-435e-b3ee-65a614f53fc7',
      title: 'Credit form',
    },
    {
      id: '951abdeb-ed18-435e-b3ee-65a614f53fc7',
      title: 'Transactions',
    },
  ],
  image_generator: [
    {
      id: 'd095f5da-28bf-4140-adad-7cc36a2b07db',
      title: 'Image Generator',
    },
  ],
  used_and_expiring_credits: [{
    id: 'ef96956b-3454-4225-bf2e-c3cd17ffd9cd',
    title: 'Used and Expiring Credits',
  }],
  news: [{
    id: 'f7eab4ed-f23f-4564-80f9-2b07c72d6dfb',
    title: 'News',
  }],
  authors: [{
    id: 'b8792734-b35c-4d36-a97c-cfb2926f722f',
    title: 'Create Author',
  }],
  news_articles: [{
    id: 'c273b6a8-8726-4b3e-bb02-035f0a1b2258',
    title: 'News Articles',
  }],
  expiring_jobs: [{
    id: '240eaaf9-981e-4b15-9316-551eaa2eea09',
    title: 'Expiring Jobs',
  }],
  qr_code_generator: [{
    id: '1a045533-9296-4a33-be03-c07633a65fe9',
    title: 'QR code generator',
  }],
  finance: [{
    id: 'f00a77d5-8c80-47b7-9dd1-159c5811235f',
    title: 'Finance',
  }],
  orders: [{
    id: '7a8938f7-6b4a-4f17-b6b2-ffe26a43e2f6',
    title: 'Orders',
  }],
  courses: [{
    id: 'd3102e80-7e0c-4b94-935a-1269f613721c',
    title: 'Courses',
  }],
  schedules: [{
    id: '6a07d70c-fe2e-4c73-875b-79215d1c4852',
    title: 'Schedules',
  }],
};

const retoolRoles: Record<
  string,
  Record<string, string[] | string>
> = {
  users: { globalRolesAll: ['users'] },
  open_ai: { globalRolesAll: ['openAI'] },
  published_jobs: { globalRolesAll: ['jobs'] },
  special_pages: { globalRolesAll: ['updateSpecialPage'] },
  tags: { globalRolesAll: ['updateTag'] },
  accounts: { globalRolesAll: ['accounts'] },
  brands: { globalRolesAll: ['updateBrand'] },
  category_keywords: { globalRolesAll: ['categoryKeywords'] },
  lookbook_images: { globalRolesAll: ['removeImage'] },
  marketplace_kpi: { globalRolesAll: ['marketplaceKPI'] },
  translate: { globalRolesAll: ['openAI'] },
  credits: { scopedRolesAll: ['accounts'] },
  image_generator: { globalRolesAll: ['openAI'] },
  used_and_expiring_credits: { globalRolesAll: ['credits'] },
  news: {
    globalRolesAny: ['updateNewsArticle', 'updateOwnNewsArticle'],
  },
  authors: { globalRolesAny: ['createAuthor', 'updateAuthor'] },
  news_articles: { globalRolesAll: ['updateNewsArticle'] },
  expiring_jobs: { globalRolesAll: ['jobs'] },
  qr_code_generator: { scopedRolesAll: ['QRCode'] },
  finance: { globalRolesAll: ['exportInvoices'] },
  orders: { globalRolesAll: ['orders'] },
  courses: { globalRolesAll: ['createCourse'] },
  schedules: { globalRolesAll: ['schedules'] },
};

const retoolPropertyTypes = {
  retoolPath: PropTypes.string.isRequired,
};

type RetoolProperties = InferProps<
typeof retoolPropertyTypes
>;

export const handleRouterMessages = (event: MessageEvent) => {
  const { method, route, query } = event.data;

  if (['pushRoute', 'replaceRoute'].includes(method)) {
    Router[method](route, query);
  }
};

const Retool = ({ retoolPath }: RetoolProperties) => {
  const accessToken = localStorage.getItem('accessToken');
  const { origin } = useSiteContext();
  const environment = isProduction(origin) ? 'production' : 'staging';
  const retoolIds = retoolPages[retoolPath];

  useHideScrollBar();

  useEffect(() => {
    window.addEventListener('message', handleRouterMessages);

    return () => {
      window.removeEventListener('message', handleRouterMessages);
    };
  });

  return (
    <>
      {retoolIds.map(({ title, id }) => (
        <iframe
          key={title}
          title={title}
          src={`https://fuww.retool.com/embedded/public/${id}?_environment=${environment}#access_token=${accessToken}`}
          style={{
            overflow: 'hidden',
            height: '100%',
            width: '100%',
            position: 'absolute',
            border: 'none',
          }}
          height="100%"
          width="100%"
        />
      ))}
    </>
  );
};

Retool.propTypes = retoolPropertyTypes;

const RetoolWithRestrictedAccess = withRestrictedAccess(Retool);

export const RetoolPage = () => {
  const router = useRouter();
  const { query: { retoolPath } } = router;
  const requiredRoles = retoolRoles[retoolPath as string];

  return (
    <AuthProvider>
      <RetoolWithRestrictedAccess
        requiredRoles={requiredRoles}
        retoolPath={retoolPath}
      />
    </AuthProvider>
  );
};

RetoolPage.getLayout = (page: React.ReactElement) => (
  <Layout>
    { page }
  </Layout>
);

export default withApollo(RetoolPage);
